export function notUndefined<T>(val: T | undefined): val is T {
  return val !== undefined;
}

export function isString(val: unknown): val is string {
  return typeof val === 'string';
}

export function isNumber(val: unknown): val is number {
  return typeof val === 'number';
}

export function iterNumberEnum<T extends Record<string, string | number>>(e: T): T[keyof T][] {
  return Object.values(e).filter(isNumber) as unknown as T[keyof T][];
}

export function iterStringEnum<T extends Record<string, string>>(e: T): T[keyof T][] {
  return Object.values(e).filter(isString) as unknown as T[keyof T][];
}

export function removeUndefined<T>(arr: (T | undefined)[]): T[] {
  return arr.filter(notUndefined);
}

export function removeUndefinedOrNullProps<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(e => e[1] !== undefined && e[1] !== null)
  ) as T;
}

export function neverHappens(_value: never, errorMessage?: string): never {
  throw new Error(errorMessage ?? `Unexpected value: ${String(_value)}`);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function neverHappensSilent(_value: never): void {}

export function asMap(value: unknown): Record<string, unknown> | undefined;
export function asMap(
  value: unknown,
  defaultValue: Record<string, unknown>
): Record<string, unknown>;
export function asMap(
  value: unknown,
  defaultValue?: Record<string, unknown>
): Record<string, unknown> | undefined {
  // eslint-disable-next-line no-null/no-null
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : defaultValue;
}
export function asMapOrThrow(value: unknown, opts?: {msg: string}): Record<string, unknown> {
  const msg = opts?.msg === undefined ? '' : ` for \`${opts.msg}\``;
  const valueAsMap = asMap(value);
  if (valueAsMap === undefined) {
    throw new Error(`Invalid value${msg}: \`${String(value)}\` is not a map`);
  }
  return valueAsMap;
}

export function asJson(value: string): Record<string, unknown> | undefined;
export function asJson(
  value: string,
  defaultValue: Record<string, unknown>
): Record<string, unknown>;
export function asJson(
  value: string,
  defaultValue?: Record<string, unknown>
): Record<string, unknown> | undefined {
  try {
    const json = JSON.parse(value);
    const res = asMap(json);
    return res ?? defaultValue;
  } catch {
    return defaultValue;
  }
}
export function asJsonOrThrow(value: string, opts?: {msg: string}): Record<string, unknown> {
  const msg = opts?.msg === undefined ? '' : ` for \`${opts.msg}\``;
  const valueAsJson = asJson(value);
  if (valueAsJson === undefined) {
    throw new Error(`Invalid value${msg}: \`${value}\` is not a valid JSON string of a map`);
  }
  return valueAsJson;
}

export function asJsonString(value: unknown): Record<string, unknown> | undefined;
export function asJsonString(
  value: unknown,
  defaultValue: Record<string, unknown>
): Record<string, unknown>;
export function asJsonString(
  value: unknown,
  defaultValue?: Record<string, unknown>
): Record<string, unknown> | undefined {
  const str = asString(value);
  return str === undefined
    ? defaultValue
    : defaultValue === undefined
      ? asJson(str)
      : asJson(str, defaultValue);
}
export function asJsonStringOrThrow(value: unknown, opts?: {msg: string}): Record<string, unknown> {
  return asJsonOrThrow(asStringOrThrow(value, opts), opts);
}

export function asString<T extends string = string>(value: unknown): T | undefined;
export function asString<T extends string = string>(value: unknown, defaultValue: string): T;
export function asString<T extends string = string>(
  value: unknown,
  defaultValue?: string
): T | undefined {
  return (typeof value === 'string' ? value : defaultValue) as T;
}
export function asStringOrThrow<T extends string = string>(
  value: unknown,
  opts?: {msg: string}
): T {
  const msg = opts?.msg === undefined ? '' : ` for \`${opts.msg}\``;
  const valueAsString = asString<T>(value);
  if (valueAsString === undefined) {
    throw new Error(`Invalid value${msg}: \`${String(value)}\` is not a string`);
  }
  return valueAsString;
}

export function asNonEmptyStringOrThrow<T extends string = string>(
  value: unknown,
  opts?: {msg: string}
): T {
  const msg = opts?.msg === undefined ? '' : ` for \`${opts.msg}\``;
  const valueAsString = asString<T>(value);
  if (valueAsString === undefined) {
    throw new Error(`Invalid value${msg}: \`${String(value)}\` is not a string`);
  }
  if (valueAsString.length === 0) {
    throw new Error(`Invalid value${msg}: \`${String(value)}\` is an empty string`);
  }
  return valueAsString;
}

export function asArray<T = unknown>(value: unknown): T[] | undefined;
export function asArray<T = unknown>(value: unknown, defaultValue: unknown[]): T[];
export function asArray<T = unknown>(value: unknown, defaultValue?: unknown[]): T[] | undefined {
  return (Array.isArray(value) ? value : defaultValue) as T[];
}
export function asArrayOrThrow<T = unknown>(value: unknown, opts?: {msg: string}): T[] {
  const msg = opts?.msg === undefined ? '' : ` for \`${opts.msg}\``;
  if (!Array.isArray(value)) {
    throw new Error(`Invalid value${msg}: \`${String(value)}\` is not an array`);
  }
  return value as T[];
}

export function asStringArray<T extends string = string>(value: unknown): T[] | undefined;
export function asStringArray<T extends string = string>(
  value: unknown,
  defaultValue: string[]
): T[];
export function asStringArray<T extends string = string>(
  value: unknown,
  defaultValue?: string[]
): T[] | undefined {
  const arr = asArray(value);
  if (arr === undefined) {
    return defaultValue as T[];
  }
  return removeUndefined(arr.map(s => asString<T>(s)));
}

export function asStringArrayOrThrow<T extends string = string>(
  value: unknown,
  opts?: {msg: string}
): T[] {
  const arr = asArrayOrThrow(value, opts);
  return arr.map((s, i) => asStringOrThrow<T>(s, {msg: `${opts?.msg} (index ${i})`}));
}

export function asStringSet<T extends string = string>(value: unknown): Set<T> | undefined;
export function asStringSet<T extends string = string>(
  value: unknown,
  defaultValue: Set<T>
): Set<T>;
export function asStringSet<T extends string = string>(
  value: unknown,
  defaultValue?: Set<T>
): Set<T> | undefined {
  const values = asStringArray(value);
  if (values === undefined) {
    return defaultValue;
  }
  return new Set<T>(values as T[]);
}

export function asStringSetOrThrow<T extends string = string>(
  value: unknown,
  opts?: {msg: string}
): Set<T> {
  return new Set<T>(asStringArrayOrThrow(value, opts));
}

export function asMapArray(value: unknown): Record<string, unknown>[] | undefined;
export function asMapArray(
  value: unknown,
  defaultValue: Record<string, unknown>[]
): Record<string, unknown>[];
export function asMapArray(
  value: unknown,
  defaultValue?: Record<string, unknown>[]
): Record<string, unknown>[] | undefined {
  const arr = asArray(value);
  if (arr === undefined) {
    return defaultValue;
  }
  return removeUndefined(arr.map(s => asMap(s)));
}
export function asMapArrayOrThrow(value: unknown, opts?: {msg: string}): Record<string, unknown>[] {
  const arr = asArrayOrThrow(value, opts);
  return arr.map((s, i) => asMapOrThrow(s, {msg: `${opts?.msg} (index ${i})`}));
}

export function asNumber<T extends number = number>(value: unknown): T | undefined;
export function asNumber<T extends number = number>(value: unknown, defaultValue: number): T;
export function asNumber<T extends number = number>(
  value: unknown,
  defaultValue?: number
): T | undefined {
  if (typeof value === 'number') {
    return (Number.isNaN(value) ? defaultValue : value) as T;
  }
  if (typeof value === 'string') {
    try {
      const parsedValue = Number.parseFloat(value);
      return (Number.isNaN(parsedValue) ? defaultValue : parsedValue) as T;
    } catch {
      return defaultValue as T;
    }
  }
  return defaultValue as T;
}
export function asNumberOrThrow<T extends number = number>(
  value: unknown,
  opts?: {msg: string}
): T {
  const msg = opts?.msg === undefined ? '' : ` for \`${opts.msg}\``;
  const valueAsNumber = asNumber(value);
  if (valueAsNumber === undefined) {
    throw new Error(`Invalid value${msg}: \`${String(value)}\` is not a number`);
  }
  return valueAsNumber as T;
}

export function asNumberArray<T extends number = number>(value: unknown): T[] | undefined;
export function asNumberArray<T extends number = number>(
  value: unknown,
  defaultValue: number[]
): T[];
export function asNumberArray<T extends number = number>(
  value: unknown,
  defaultValue?: number[]
): T[] | undefined {
  const arr = asArray(value);
  if (arr === undefined) {
    return defaultValue as T[];
  }
  return removeUndefined(arr.map(s => asNumber<T>(s)));
}

export function asNumberArrayOrThrow<T extends number = number>(
  value: unknown,
  opts?: {msg: string}
): T[] {
  const arr = asArrayOrThrow(value, opts);
  return arr.map((s, i) => asNumberOrThrow<T>(s, {msg: `${opts?.msg} (index ${i})`}));
}

export function asNumberSet<T extends number = number>(value: unknown): Set<T> | undefined;
export function asNumberSet<T extends number = number>(
  value: unknown,
  defaultValue: Set<T>
): Set<T>;
export function asNumberSet<T extends number = number>(
  value: unknown,
  defaultValue?: Set<T>
): Set<T> | undefined {
  const values = asNumberArray(value);
  if (values === undefined) {
    return defaultValue;
  }
  return new Set<T>(values as T[]);
}

export function asNumberSetOrThrow<T extends number = number>(
  value: unknown,
  opts?: {msg: string}
): Set<T> {
  return new Set<T>(asNumberArrayOrThrow(value, opts));
}

export function asBoolean(value: unknown): boolean | undefined;
export function asBoolean(value: unknown, defaultValue: boolean): boolean;
export function asBoolean(value: unknown, defaultValue?: boolean): boolean | undefined {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return !Number.isNaN(value) ? value !== 0 : false;
  }
  if (typeof value === 'string') {
    if (value === '0' || value === 'false') {
      return false;
    }
    if (value === '1' || value === 'true') {
      return true;
    }
    return defaultValue;
  }
  return defaultValue;
}

export function asBooleanOrThrow(value: unknown, opts?: {msg: string}): boolean {
  const msg = opts?.msg === undefined ? '' : ` for \`${opts.msg}\``;
  const valueAsBoolean = asBoolean(value);
  if (valueAsBoolean === undefined) {
    throw new Error(`Invalid value${msg}: \`${String(value)}\` is not a boolean`);
  }
  return valueAsBoolean;
}

const TIMESTAMP_REGEX = /^[0-9]{1,15}$/u;
export function asDate(value: unknown): Date | undefined;
export function asDate(value: unknown, defaultValue: Date): Date;
export function asDate(value: unknown, defaultValue?: Date): Date | undefined {
  const date =
    value instanceof Date
      ? value
      : new Date(
          typeof value === 'string' && TIMESTAMP_REGEX.test(value)
            ? Number.parseFloat(value)
            : String(value)
        );
  return Number.isNaN(date.getTime()) ? defaultValue : date;
}

export function asDateOrThrow(value: unknown, opts?: {msg: string}): Date {
  const msg = opts?.msg === undefined ? '' : ` for \`${opts.msg}\``;
  const valueAsDate = asDate(value);
  if (valueAsDate === undefined) {
    throw new Error(`Invalid value${msg}: \`${String(value)}\` cannot be parsed as a Date`);
  }
  return valueAsDate;
}

// export function asDate(value: unknown): Date | undefined;
// export function asDate(value: unknown, defaultValue: Date): Date;
// export function asDate(value: unknown, defaultValue?: Date): Date | undefined {
//   if (typeof value === 'number') {
//     return new Date(value);
//   }
//   return value instanceof Date ? value : defaultValue;
// }

export function isNull<T>(val: T | null): val is null {
  // eslint-disable-next-line no-null/no-null
  return val === null;
}

export function asError(err: unknown, messagePrefix = ''): Error {
  const errObj =
    err instanceof Error ? err : new Error(typeof err === 'string' ? err : String(err));
  errObj.message = `${messagePrefix}${errObj.message}`;
  return errObj;
}

export function errorAsString(err: unknown): string {
  const errorMap = asMap(err);
  if (errorMap === undefined) {
    return asString(err) ?? String(err);
  }

  const errorMessage = asString(errorMap['message']);
  if (errorMessage === undefined) {
    return String(err);
  }
  return errorMessage;
}

export function errorAndStackAsString(err: unknown): string {
  const errorMap = asMap(err);
  if (errorMap === undefined) {
    return asString(err) ?? String(err);
  }

  const stack = asString(errorMap['stack']);
  if (stack === undefined) {
    return String(err);
  }
  return stack;
}

export function asConstantOrThrow<T>(value: unknown, expected: T, opts?: {msg: string}): T {
  const msg = opts?.msg === undefined ? '' : ` for ${opts.msg}`;
  if (value !== expected) {
    throw new Error(`Invalid value${msg}: \`${String(value)}\`, expected \`${String(expected)}\``);
  }
  return value as T;
}

// export function asParsedJson<T>(json: string): T {
//   try {
//     return JSON.parse(json) as T;
//   } catch {
//     const defaultValue = {};
//     return defaultValue as T;
//   }
// }
export function parseJson(
  json: string
): {res: unknown; err: undefined} | {res: undefined; err: unknown} {
  try {
    return {res: JSON.parse(json), err: undefined};
  } catch (err: unknown) {
    return {err, res: undefined};
  }
}
export type Brand<Type, Name> = Type & {__brand: Name};
export type StringBrand = string & {__brand: string};
export type NumberBrand = number & {__brand: number};

// biome-ignore lint/suspicious/noExplicitAny: <no-explanation>
export type AnyBrand = (string | number) & {__brand: any};

export type Untrusted<T> = T extends // biome-ignore lint/complexity/noBannedTypes: <no-explanation>
  | Function
  | Date
  | boolean
  | number
  | string
  | undefined
  | null
  | unknown[]
  ? unknown
  : {[P in keyof T]?: Untrusted<T[P]>};

export type DeepPartial<T> = T extends // biome-ignore lint/complexity/noBannedTypes: <no-explanation>
  | Function
  | Date
  | boolean
  | number
  | string
  | undefined
  | null
  | unknown[]
  ? T
  : {[P in keyof T]?: DeepPartial<T[P]>};

export type MapInterface<I, Type> = {[Key in keyof I]: Type};
// Get all the keys of a type including the optional attributes
type NonHomomorphicKeys<T> = ({[P in keyof T]: P} & None)[keyof T];
export type MapInterfaceStrict<I, Type> = {[Key in NonHomomorphicKeys<I>]: Type};

type KeysOfType<T, Type> = {[Key in keyof T]: T[Key] extends Type ? Key : never}[keyof T];
export type RestrictInterface<T, Type> = Pick<T, KeysOfType<T, Type>>;

interface RecursiveArray<T> extends Array<T | RecursiveArray<T>> {}
export type NestedArray<T> = (T | RecursiveArray<T>)[];

// Type for an empty object (ie: {})
export type None = Record<string, never>;
export type Empty = None;
export type NoProps = None;

export type NonEmptyArray<T> = [T, ...T[]];

export type AddPrefix<T, P extends string> = {
  [K in keyof T as K extends string ? `${P}${K}` : never]: T[K];
};
export function addPrefix<T extends Record<string, unknown>, Prefix extends string>(
  attr: T,
  prefix: Prefix
): AddPrefix<T, Prefix> {
  return Object.fromEntries(
    Object.entries(attr).map(([key, value]) => [`${prefix}${key}`, value])
  ) as AddPrefix<T, Prefix>;
}

export type WithNull<T> = {
  [Key in keyof T]: T[Key] extends Exclude<T[Key], undefined> ? T[Key] : T[Key] | null;
};
type Id<T> = T;
export type Flatten<T> = Id<{[k in keyof T]: T[k]}>;

export type Defined<T> = Exclude<T, undefined>;

// biome-ignore lint/suspicious/noExplicitAny: <no-explanation>
type Contra<T> = T extends any ? (arg: T) => void : never;
type InferContra<T> = [T] extends [(arg: infer I) => void] ? I : never;
type PickOne<T> = InferContra<InferContra<Contra<Contra<T>>>>;
export type UnionToTuple<T> = PickOne<T> extends infer U // assign PickOne<T> to U
  ? Exclude<T, U> extends never // T and U are the same
    ? [T]
    : [...UnionToTuple<Exclude<T, U>>, U] // recursion
  : never;

export type DeepReadonly<T> = {
  readonly [K in keyof T]: T[K] extends object ? DeepReadonly<T[K]> : T[K];
};

// Opposite of Partial<T>
export type Strict<T> = {
  [P in keyof T]-?: T[P];
};

export type StringAttributes<T> = Exclude<
  {[K in keyof T]: T[K] extends string | undefined ? K : never}[keyof T],
  undefined
>;

// Enforce all properties to be specified even the optional ones.
// The value `undefined` will need to be explicitly specified instead of ommited.
// biome-ignore lint/suspicious/noExplicitAny: type hack
export type Exhaustive<T> = {[P in keyof T & keyof any]: T[P]};

// Extract the optional keys from a type
export type OptionalOrUndefinedKeys<T> = {
  [K in keyof T]-?: undefined extends T[K] ? K : never;
}[keyof T];

type NonUndefined<T> = T extends undefined ? never : T;
type OptionalKeys<T> = {
  [K in keyof T]-?: undefined extends T[K] ? K : never;
}[keyof T];
export type UndefinedToOptional<T> = {
  // required part
  [K in Exclude<keyof T, OptionalKeys<T>>]: T[K];
} & {
  // optional part, re-emitted to strip `| undefined`
  [K in OptionalKeys<T>]?: NonUndefined<T[K]>;
} extends infer O
  ? {
      [K in keyof O]: O[K]; // remap to rebuild
    }
  : never;
